import React, { useState, useEffect, useCallback } from 'react';
import { Select, Typography, AutoComplete, Spin, Alert, Divider } from 'antd';
import { EntityType } from './types';
import { fetchEntities } from './utils';
import { apiService } from '../../services/api';
import { useFieldConfig } from '../../contexts/FieldConfigContext';
import { formatEntitlementLabel } from '../../utils/utils';

const { Option } = Select;
const { Title, Text } = Typography;

interface EntitySelectorProps {
  selectedEntity: EntityType | null;
  onEntitySelect: (entity: EntityType | null) => void;
  onEntityDataSelect: (data: Record<string, unknown> | null) => void;
}

const ENTITY_OPTIONS = [
  { value: EntityType.PRODUCT, label: 'Product Details', description: 'Update name, key, provisioning client & style' },
  { value: EntityType.ENTITLEMENT, label: 'Entitlements', description: 'Update standard entitlements' },
  { value: EntityType.ORG_ENTITLEMENT, label: 'Org Entitlements', description: 'Update organization-level entitlements' },
  { value: EntityType.DEPENDENT_SERVICE, label: 'Dependent Service', description: 'Manage service-enabled org entitlements' },
  { value: EntityType.SKU_STANDALONE, label: 'SKU - Standalone', description: 'Update standalone SKU details' },
  { value: EntityType.SKU_SUITE, label: 'SKU - Suite', description: 'Update suite SKU details' },
  { value: EntityType.SKU_ADDON, label: 'SKU - Add-On', description: 'Update add-on SKU details' },
];

const EntitySelector: React.FC<EntitySelectorProps> = ({
  selectedEntity,
  onEntitySelect,
  onEntityDataSelect,
}) => {
  const { isEntitySelectionDisabled } = useFieldConfig();
  const [entities, setEntities] = useState<Array<Record<string, unknown>>>([]);
  const [products, setProducts] = useState<Array<Record<string, unknown>>>([]);
  const [selectedProduct, setSelectedProduct] = useState<Record<string, unknown> | null>(null);
  const [selectedItem, setSelectedItem] = useState<Record<string, unknown> | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [productSearchValue, setProductSearchValue] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const requiresProductFilter = (entityType: EntityType | null): boolean => {
    return entityType === EntityType.ENTITLEMENT || entityType === EntityType.ORG_ENTITLEMENT;
  };

  const isSKUEntityType = (entityType: EntityType | null): boolean => {
    return (
      entityType === EntityType.SKU_STANDALONE ||
      entityType === EntityType.SKU_SUITE ||
      entityType === EntityType.SKU_ADDON
    );
  };

  const isDependentService = (entityType: EntityType | null): boolean => {
    return entityType === EntityType.DEPENDENT_SERVICE;
  };

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const data = await fetchEntities(EntityType.PRODUCT);
      setProducts(data as Array<Record<string, unknown>>);
    } catch (error) {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadEntities = useCallback(async () => {
    if (!selectedEntity) {
      return;
    }

    // If entity requires product filter but no product selected, don't load
    if (requiresProductFilter(selectedEntity) && !selectedProduct) {
      return;
    }

    setLoading(true);
    try {
      // Handle DEPENDENT_SERVICE: Load only org entitlements where is_service = true
      if (isDependentService(selectedEntity)) {
        const allOrgEntitlements = await apiService.getOrgEntitlements();
        const serviceEntitlements = allOrgEntitlements.filter(
          (ent) => ent.is_service === true
        );
        setEntities(serviceEntitlements as unknown as Array<Record<string, unknown>>);
        setLoading(false);
        return;
      }

      const data = await fetchEntities(selectedEntity);

      // Filter by product ID if product is selected
      if (selectedProduct && requiresProductFilter(selectedEntity)) {
        const productId = String(selectedProduct.ID || selectedProduct.id);
        const filtered = (data as Array<Record<string, unknown>>).filter(
          (item) => String(item.product_id) === productId
        );
        setEntities(filtered);
      } else if (isSKUEntityType(selectedEntity)) {
        // Filter SKUs based on entity type
        const [productSkuConfigs, productAddonConfigs] = await Promise.all([
          apiService.getProductSkuConfigs(),
          apiService.getProductAddonConfigs()
        ]);

        // Count products per SKU
        const skuProductCounts = new Map<number, number>();
        productSkuConfigs.forEach((config) => {
          const skuId = config.sku_id;
          skuProductCounts.set(skuId, (skuProductCounts.get(skuId) || 0) + 1);
        });

        // Get addon SKU IDs
        const addonSkuIds = new Set(
          productAddonConfigs.map((config) => config.sku_id)
        );

        // Filter based on entity type
        const filtered = (data as Array<Record<string, unknown>>).filter((sku) => {
          const skuId = Number(sku.ID || sku.id);
          const productCount = skuProductCounts.get(skuId) || 0;

          switch (selectedEntity) {
            case EntityType.SKU_STANDALONE:
              return productCount === 1 && !addonSkuIds.has(skuId);
            case EntityType.SKU_SUITE:
              return productCount > 1;
            case EntityType.SKU_ADDON:
              return addonSkuIds.has(skuId);
            default:
              return true;
          }
        });

        setEntities(filtered);
      } else {
        setEntities(data as Array<Record<string, unknown>>);
      }
    } catch (error) {
      setEntities([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, selectedProduct]);

  useEffect(() => {
    if (selectedEntity) {
      if (requiresProductFilter(selectedEntity)) {
        void loadProducts();
      } else if (isDependentService(selectedEntity)) {
        void loadEntities();
      } else {
        void loadEntities();
      }
    } else {
      setEntities([]);
      setProducts([]);
      setSelectedProduct(null);
      setSelectedItem(null);
      setSearchValue('');
      setProductSearchValue('');
    }
  }, [selectedEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedProduct && requiresProductFilter(selectedEntity)) {
      void loadEntities();
    }
  }, [selectedProduct, selectedEntity, loadEntities]);

  const handleEntityTypeChange = (value: EntityType) => {
    onEntitySelect(value);
    setSelectedProduct(null);
    setSelectedItem(null);
    setSearchValue('');
    setProductSearchValue('');
    setEntities([]);
  };

  const handleProductSelect = (value: string) => {
    const product = products.find((p) => getItemId(p) === value);
    if (product) {
      setSelectedProduct(product);
      setProductSearchValue(getEntityLabel(product, EntityType.PRODUCT));
      setSelectedItem(null);
      setSearchValue('');
    }
  };

  const handleItemSelect = (value: string) => {
    const item = entities.find((e) => getItemId(e) === value);
    if (item) {
      setSelectedItem(item);
      setSearchValue(getItemLabel(item));
      onEntityDataSelect(item);
    }
  };

  const getItemId = (item: Record<string, unknown> | null): string => {
    if (!item) {
      return '';
    }
    return String(item.ID || item.id || '');
  };

  const getEntityLabel = (item: Record<string, unknown> | null, _entityType?: EntityType): string => {
    if (!item) {
      return '';
    }
    // Use the item's name field for all entity types
    const label = item.name as string || '';
    // Fallback to ID if label is empty
    return label || `ID: ${getItemId(item)}`;
  };

  const getItemLabel = (item: Record<string, unknown> | null): string => {
    if (
      selectedEntity === EntityType.ENTITLEMENT ||
      selectedEntity === EntityType.ORG_ENTITLEMENT ||
      selectedEntity === EntityType.DEPENDENT_SERVICE
    ) {
      if (!item) { return ''; }
      return formatEntitlementLabel({
        name: String(item.name || ''),
        value: String(item.value || ''),
        description: item.description ? String(item.description) : undefined,
        needs_quantity: Boolean(item.needs_quantity),
        unit: item.unit ? String(item.unit) : undefined,
      });
    }
    return getEntityLabel(item, selectedEntity || undefined);
  };

  const getItemLabelWithDescription = (item: Record<string, unknown> | null): string => {
    if (!item) {
      return '';
    }
    const name = item.name as string || '';
    const description = item.description as string || '';
    return description ? `${name} - ${description}` : name;
  };

  const searchOptions = entities.map((item) => ({
    value: getItemId(item),
    label: isDependentService(selectedEntity) ? getItemLabelWithDescription(item) : getItemLabel(item),
  }));

  return (
    <div>
      <Alert
        message="Update Existing Configurations"
        description="Choose what you want to modify. For Product, Entitlements, and Org Entitlements, you'll need to select the product first."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>Step 1: Choose what to update</Title>

      <div style={{ marginBottom: 16 }}>
        <Text strong>
          <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
          Select Entity to Modify
        </Text>
        <Select
          style={{ width: '100%', marginTop: 8 }}
          placeholder="Choose entity to update"
          size="large"
          value={selectedEntity || undefined}
          onChange={handleEntityTypeChange}
        >
          {ENTITY_OPTIONS
            .filter((option) => !isEntitySelectionDisabled(option.value))
            .map((option) => (
              <Option key={option.value} value={option.value}>
                <div>
                  <Text strong>{option.label}</Text>
                  <div style={{ fontSize: '12px', color: '#999' }}>{option.description}</div>
                </div>
              </Option>
            ))}
        </Select>
      </div>

      {/* Step 2: Product selector - for Product, Entitlements, and Org Entitlements */}
      {selectedEntity && requiresProductFilter(selectedEntity) && (
        <>
          <Divider />
          <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>Step 2: Select the product</Title>

          <div style={{ marginBottom: 16 }}>
            <Text strong>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
              Select Product to Modify
            </Text>
            <AutoComplete
              style={{ width: '100%', marginTop: 8 }}
              options={products.map((p) => ({
                value: getItemId(p),
                label: getEntityLabel(p, EntityType.PRODUCT),
              }))}
              value={productSearchValue}
              onChange={(value) => {
                setProductSearchValue(value);
                if (!value) {
                  setSelectedProduct(null);
                }
              }}
              onSelect={handleProductSelect}
              placeholder="Search for product..."
              notFoundContent={loadingProducts ? <Spin size="small" /> : 'No products found'}
              filterOption={(inputValue, option) =>
                option?.label?.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
              allowClear
            />
          </div>

          {selectedProduct && (
            <Alert
              message="Product Selected"
              description={`You're about to modify ${selectedEntity === EntityType.PRODUCT ? 'product details' : selectedEntity === EntityType.ENTITLEMENT ? 'entitlements' : 'org entitlements'} for the selected product.`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Show entity selector after product is selected */}
          {selectedProduct && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                Select {ENTITY_OPTIONS.find((o) => o.value === selectedEntity)?.label}
              </Text>
              <AutoComplete
                style={{ width: '100%', marginTop: 8 }}
                dropdownStyle={{ maxWidth: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}
                options={searchOptions}
                value={searchValue}
                onChange={setSearchValue}
                onSelect={handleItemSelect}
                placeholder={`Search by name... (${entities.length} items found)`}
                notFoundContent={loading ? <Spin size="small" /> : 'No items found'}
                filterOption={(inputValue, option) =>
                  option?.label?.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
                allowClear
              />
              {loading && <Spin style={{ marginTop: 8 }} />}
            </div>
          )}
        </>
      )}

      {/* Step 2: Direct entity selector - for Product, SKU types (no product filter needed) */}
      {selectedEntity && selectedEntity === EntityType.PRODUCT && (
        <>
          <Divider />
          <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>Step 2: Select the product</Title>

          <div style={{ marginBottom: 16 }}>
            <Text strong>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
              Select Product to Modify
            </Text>
            <AutoComplete
              style={{ width: '100%', marginTop: 8 }}
              options={searchOptions}
              value={searchValue}
              onChange={setSearchValue}
              onSelect={handleItemSelect}
              placeholder="Search by name..."
              notFoundContent={loading ? <Spin size="small" /> : 'No items found'}
              filterOption={(inputValue, option) =>
                option?.label?.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
              allowClear
            />
          </div>

          {selectedItem && (
            <Alert
              message="Product Selected"
              description="You're about to modify product details for the selected product."
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </>
      )}

      {/* Step 2: Direct entity selector - for SKU types (no product filter needed) */}
      {selectedEntity && isSKUEntityType(selectedEntity) && (
        <>
          <Divider />
          <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>Step 2: Select the SKU</Title>

          <div style={{ marginBottom: 16 }}>
            <Text strong>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
              Select {ENTITY_OPTIONS.find((o) => o.value === selectedEntity)?.label} to Modify
            </Text>
            <AutoComplete
              style={{ width: '100%', marginTop: 8 }}
              options={searchOptions}
              value={searchValue}
              onChange={setSearchValue}
              onSelect={handleItemSelect}
              placeholder="Search by name..."
              notFoundContent={loading ? <Spin size="small" /> : 'No items found'}
              filterOption={(inputValue, option) =>
                option?.label?.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
              allowClear
            />
          </div>

          {selectedItem && (
            <Alert
              message={`${ENTITY_OPTIONS.find((o) => o.value === selectedEntity)?.label} Selected`}
              description={`You're about to modify details for the selected ${ENTITY_OPTIONS.find((o) => o.value === selectedEntity)?.label?.toLowerCase()}.`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </>
      )}

      {/* Step 2: Service-enabled Org Entitlements selector - for DEPENDENT_SERVICE */}
      {selectedEntity && isDependentService(selectedEntity) && (
        <>
          <Divider />
          <Title level={5} style={{ marginTop: 20, marginBottom: 12 }}>Step 2: Select Service-Enabled Org Entitlement</Title>

          <div style={{ marginBottom: 16 }}>
            <Text strong>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
              Select Dependent Service Org Entitlement
            </Text>
            <AutoComplete
              style={{ width: '100%', marginTop: 8 }}
              options={searchOptions}
              value={searchValue}
              onChange={setSearchValue}
              onSelect={handleItemSelect}
              placeholder={`Search by name... (${entities.length} service entitlements found)`}
              notFoundContent={loading ? <Spin size="small" /> : 'No service-enabled org entitlements found'}
              filterOption={(inputValue, option) =>
                option?.label?.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
              allowClear
            />
            {loading && <Spin style={{ marginTop: 8 }} />}
          </div>

          {selectedItem && (
            <Alert
              message="Service Entitlement Selected"
              description={`You're about to manage dependent services for "${getItemLabel(selectedItem)}".`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EntitySelector;
